import React from 'react';
import Box from '../../components/Common/Box';

const Toggle = ({ checked, onChange, disabled, label }: { checked: boolean, onChange: (v: boolean) => void, disabled?: boolean, label: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0' }}>
        <span style={{ color: 'var(--text-main)', fontWeight: 500, fontSize: '14px' }}>{label}</span>
        <div 
            onClick={() => !disabled && onChange(!checked)}
            style={{ 
                width: '44px', 
                height: '24px', 
                backgroundColor: checked ? '#48bb78' : '#cbd5e0', 
                borderRadius: '12px', 
                position: 'relative', 
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: disabled ? 0.6 : 1
            }}
        >
            <div style={{ 
                width: '18px', 
                height: '18px', 
                backgroundColor: 'white', 
                borderRadius: '50%', 
                position: 'absolute', 
                top: '3px', 
                left: checked ? '23px' : '3px',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }} />
        </div>
    </div>
);

interface DesktopProfileProps {
    user: any;
    theme: string;
    isEditing: boolean;
    setIsEditing: (v: boolean) => void;
    fullName: string;
    setFullName: (v: string) => void;
    pseudonym: string;
    setPseudonym: (v: string) => void;
    isOptIn: boolean;
    setIsOptIn: (v: boolean) => void;
    showRealName: boolean;
    setShowRealName: (v: boolean) => void;
    loading: boolean;
    successMsg: string;
    errorMsg: string;
    showConfirmReset: boolean;
    handleToggleTheme: () => void;
    handleSaveProfile: () => void;
    handleResetPassword: () => void;
    handleUpdateSocialSettings: (optIn: boolean, ps: string, sr?: boolean) => void;
    setShowConfirmReset: (v: boolean) => void;
    logout: () => void;
    getInitials: (name: string) => string;
    getPlanName: (roleName?: string) => string;
    formatDate: (dateString?: string) => string;
}

const DesktopProfile: React.FC<DesktopProfileProps> = ({
    user, theme, isEditing, setIsEditing, fullName, setFullName,
    pseudonym, setPseudonym, isOptIn, setIsOptIn, showRealName, setShowRealName, loading,
    successMsg, errorMsg, showConfirmReset,
    handleToggleTheme, handleSaveProfile, handleResetPassword, handleUpdateSocialSettings,
    setShowConfirmReset, logout, getInitials, getPlanName, formatDate
}) => {
    return (
        <div style={{ maxWidth: '800px', margin: '40px auto', padding: '0 20px' }}>
            <Box>
                <div style={{ padding: '40px' }}>
                    {/* Notifications */}
                    {successMsg && (
                        <div style={{ padding: '12px 20px', backgroundColor: '#f0fff4', color: '#2f855a', borderRadius: '8px', marginBottom: '20px', border: '1px solid #c6f6d5', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <i className="material-icons" style={{ fontSize: '20px' }}>check_circle</i>
                            {successMsg}
                        </div>
                    )}
                    {errorMsg && (
                        <div style={{ padding: '12px 20px', backgroundColor: '#fff5f5', color: '#c53030', borderRadius: '8px', marginBottom: '20px', border: '1px solid #fed7d7', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <i className="material-icons" style={{ fontSize: '20px' }}>error</i>
                            {errorMsg}
                        </div>
                    )}

                    {/* Avatar Section */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '40px' }}>
                        <div style={{ position: 'relative', marginBottom: '20px' }}>
                            <div style={{ width: '120px', height: '120px', borderRadius: '50%', backgroundColor: '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px', color: 'white', fontWeight: 'bold', border: '4px solid white', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                                {user?.full_name ? getInitials(user.full_name) : 'U'}
                            </div>
                            <button style={{ position: 'absolute', bottom: '0', right: '0', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }} title='Changer la photo'>📷</button>
                        </div>
                        <h2 style={{ margin: '0 0 5px 0', color: 'var(--text-heading)' }}>{user?.full_name || 'Utilisateur'}</h2>
                        <p style={{ margin: 0, color: '#666' }}>{user?.email || 'email@example.com'}</p>
                        <div style={{ marginTop: '10px' }}>
                            <span style={{ backgroundColor: user?.is_active ? '#e6fffa' : '#fff5f5', color: user?.is_active ? '#38b2ac' : '#e53e3e', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', marginRight: '10px' }}>
                                {user?.is_active ? 'Compte Actif' : 'Inactif'}
                            </span>
                            <span style={{ backgroundColor: user?.is_email_verified ? '#ebf8ff' : '#fff5f5', color: user?.is_email_verified ? '#3182ce' : '#e53e3e', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>
                                {user?.is_email_verified ? 'Email Vérifié ✓' : 'Email Non Vérifié ⚠️'}
                            </span>
                        </div>
                    </div>

                    {/* Details Section */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '40px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)', fontSize: '14px' }}>Nom Complet</label>
                            {isEditing ? (
                                <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-input)', fontSize: '14px', color: 'var(--text-heading)' }} />
                            ) : (
                                <div style={{ padding: '12px', backgroundColor: 'var(--bg-input)', borderRadius: '8px', color: 'var(--text-heading)', fontWeight: 500 }}>{user?.full_name || '—'}</div>
                            )}
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)', fontSize: '14px' }}>Email</label>
                            <div style={{ padding: '12px', backgroundColor: 'var(--bg-input)', borderRadius: '8px', color: 'var(--text-heading)', fontWeight: 500 }}>{user?.email || '—'}</div>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)', fontSize: '14px' }}>Abonnement (Plan Actuel)</label>
                            <div style={{ padding: '12px', backgroundColor: 'rgba(49, 130, 206, 0.1)', borderRadius: '8px', color: '#63b3ed', fontWeight: 'bold', border: '1px solid rgba(49, 130, 206, 0.2)' }}>
                                {getPlanName(user?.role?.name)}
                            </div>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)', fontSize: '14px' }}>Expiration de l'abonnement</label>
                            <div style={{ padding: '12px', backgroundColor: 'var(--bg-input)', borderRadius: '8px', color: 'var(--text-heading)', fontWeight: 500 }}>
                                {user?.role?.name === 'global_admin' ? 'À vie (Administrateur)' : formatDate(user?.subscription_end_date)}
                            </div>
                        </div>
                        {user?.tenant?.name && (
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)', fontSize: '14px' }}>Organisation (B2B)</label>
                                <div style={{ padding: '12px', backgroundColor: 'rgba(56, 161, 105, 0.1)', borderRadius: '8px', color: '#4ade80', fontWeight: 500, border: '1px solid rgba(56, 161, 105, 0.2)' }}>
                                    {user.tenant.name} {user.tenant.plan ? `(Plan: ${user.tenant.plan})` : ''}
                                </div>
                            </div>
                        )}

                        {/* Social & Competition */}
                        <div style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
                            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: 'var(--text-heading)', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>Communauté & Privacy</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxWidth: '450px' }}>
                                
                                <Toggle 
                                    label="Je ne souhaite pas participer au Leaderboard public"
                                    checked={!isOptIn}
                                    disabled={loading}
                                    onChange={(val: boolean) => {
                                        const newOptIn = !val;
                                        setIsOptIn(newOptIn);
                                        handleUpdateSocialSettings(newOptIn, pseudonym);
                                    }}
                                />

                                <Toggle 
                                    label="Afficher mon vrai nom sur le forum"
                                    checked={showRealName}
                                    disabled={loading}
                                    onChange={(val: boolean) => {
                                        setShowRealName(val);
                                        handleUpdateSocialSettings(isOptIn, pseudonym, val);
                                    }}
                                />
                                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '-5px', marginBottom: '15px' }}>
                                    {showRealName ? "Votre nom complet sera visible sur vos messages." : "Votre pseudonyme sera utilisé par défaut sur le forum."}
                                </p>

                                {isOptIn && (
                                    <div style={{ marginTop: '10px', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)', fontSize: '14px' }}>Pseudonyme (utilisé pour le classement et forum)</label>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <input
                                                type="text"
                                                value={pseudonym}
                                                onChange={(e) => setPseudonym(e.target.value)}
                                                placeholder="Ex: LionDuPlateau"
                                                disabled={loading}
                                                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-input)', fontSize: '14px', color: 'var(--text-heading)' }}
                                            />
                                            {pseudonym !== (user?.social_profile?.pseudonym || '') && (
                                                <button
                                                    onClick={() => handleUpdateSocialSettings(isOptIn, pseudonym)}
                                                    disabled={loading || !pseudonym}
                                                    style={{ padding: '0 15px', borderRadius: '8px', backgroundColor: '#48bb78', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                                                >
                                                    Mettre à jour
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Display Preferences */}
                        <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
                            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: 'var(--text-main)', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>Préférences d'Affichage</h3>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px', backgroundColor: 'var(--bg-hover)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                                <div>
                                    <strong style={{ color: 'var(--text-main)', display: 'block', marginBottom: '4px' }}>Thème de l'Application</strong>
                                    <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Choisissez entre le mode clair ou le mode sombre.</span>
                                </div>
                                <button onClick={handleToggleTheme} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '20px', backgroundColor: theme === 'dark' ? 'var(--icon-bg)' : '#ffffff', color: 'var(--text-heading)', border: '1px solid var(--border-color)', cursor: 'pointer', fontWeight: 600, boxShadow: '0 2px 4px rgba(0,0,0,0.05)', transition: 'all 0.3s ease' }}>
                                    {theme === 'dark' ? (<><span>☀️</span> Retour au Thème Clair</>) : (<><span>🌙</span> Activer le Thème Sombre</>)}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Actions Section */}
                    <div style={{ borderTop: '1px solid #eee', paddingTop: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <button disabled={loading} onClick={handleResetPassword} style={{ background: 'none', border: '1px solid #ddd', padding: '10px 20px', borderRadius: '6px', cursor: loading ? 'not-allowed' : 'pointer', color: showConfirmReset ? '#c53030' : '#666', borderColor: showConfirmReset ? '#feb2b2' : '#ddd', backgroundColor: showConfirmReset ? '#fff5f5' : 'transparent', fontWeight: 500, opacity: loading ? 0.7 : 1, transition: 'all 0.2s', textAlign: 'left' }}>
                                {loading ? 'Envoi...' : (showConfirmReset ? "⚠️ Cliquez pour envoyer l'email" : 'Modifier le mot de passe')}
                            </button>
                            {showConfirmReset && (
                                <button onClick={() => setShowConfirmReset(false)} style={{ background: 'none', border: 'none', color: '#718096', fontSize: '12px', cursor: 'pointer', textAlign: 'left', textDecoration: 'underline' }}>Annuler</button>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '15px' }}>
                            {isEditing ? (
                                <button disabled={loading} onClick={handleSaveProfile} style={{ background: '#48bb78', color: 'white', border: 'none', padding: '10px 25px', borderRadius: '6px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 500, boxShadow: '0 4px 6px rgba(72, 187, 120, 0.25)' }}>Enregistrer</button>
                            ) : (
                                <button onClick={() => setIsEditing(true)} style={{ background: '#667eea', color: 'white', border: 'none', padding: '10px 25px', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, boxShadow: '0 4px 6px rgba(102, 126, 234, 0.25)' }}>Modifier le profil</button>
                            )}
                            <button onClick={logout} style={{ background: 'rgba(229, 62, 62, 0.1)', color: '#f87171', border: '1px solid rgba(229, 62, 62, 0.2)', padding: '10px 25px', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}>Déconnexion</button>
                        </div>
                    </div>
                </div>
            </Box>
        </div>
    );
};

export default DesktopProfile;
